import React, { useState } from 'react';
import styled from 'styled-components';
import { signInWithEmailAndPassword, signOut, User } from 'firebase/auth';
import { statsAuth } from '../visitorStatsFirebase';

interface UserCountLoginProps {
  user: User | null;
  checkingAuth?: boolean;
}

const Page = styled.main`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at 15% 15%, rgba(196, 177, 118, 0.22), transparent 32%),
    radial-gradient(circle at 85% 85%, rgba(76, 102, 62, 0.18), transparent 34%),
    #f5f4ef;
  color: #171916;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const LoginCard = styled.section`
  width: min(100%, 430px);
  padding: 44px;
  box-sizing: border-box;
  border: 1px solid rgba(28, 37, 25, 0.09);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 24px 70px rgba(29, 36, 25, 0.1);
  text-align: center;
  backdrop-filter: blur(14px);

  @media (max-width: 520px) {
    padding: 34px 24px;
  }
`;

const Logo = styled.div`
  width: 54px;
  height: 54px;
  display: grid;
  place-items: center;
  margin: 0 auto 26px;
  border-radius: 18px;
  background: #1c2519;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -1px;
`;

const Eyebrow = styled.span`
  color: #71805c;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 11px 0 13px;
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -1.2px;
`;

const Description = styled.p`
  margin: 0 0 28px;
  color: #70756c;
  font-size: 14px;
  line-height: 1.7;
`;

const LoginButton = styled.button`
  width: 100%;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 11px;
  border: 1px solid #dfe1da;
  border-radius: 12px;
  background: #fff;
  color: #252923;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 650;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #aeb7a6;
    box-shadow: 0 8px 24px rgba(29, 36, 25, 0.08);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 14px;
`;

const Field = styled.label`
  display: grid;
  gap: 8px;
  text-align: left;

  span {
    color: #555b52;
    font-size: 12px;
    font-weight: 650;
  }

  input {
    width: 100%;
    min-height: 49px;
    padding: 0 15px;
    box-sizing: border-box;
    border: 1px solid #dfe1da;
    border-radius: 12px;
    background: #fff;
    color: #20241e;
    font: inherit;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &::placeholder { color: #a6aaa2; }
    &:focus {
      border-color: #71805c;
      box-shadow: 0 0 0 3px rgba(113, 128, 92, 0.12);
    }
  }
`;

const ErrorMessage = styled.p`
  margin: 16px 0 0;
  color: #a23f36;
  font-size: 13px;
`;

const SecurityNote = styled.p`
  margin: 24px 0 0;
  color: #999d95;
  font-size: 11px;
  line-height: 1.6;
`;

const UserCountLogin: React.FC<UserCountLoginProps> = ({ user, checkingAuth = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await signInWithEmailAndPassword(statsAuth, email.trim(), password);
    } catch (caught) {
      const code = typeof caught === 'object' && caught && 'code' in caught
        ? String(caught.code)
        : '';
      setError(
        code === 'auth/too-many-requests'
          ? '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해 주세요.'
          : '이메일 또는 비밀번호가 올바르지 않습니다.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setSubmitting(true);
    setError('');
    try {
      await signOut(statsAuth);
    } catch {
      setError('로그아웃하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <LoginCard>
        <Logo>V</Logo>
        <Eyebrow>VUZA GROUP · PRIVATE</Eyebrow>
        <Title>Visitor analytics</Title>
        <Description>
          {checkingAuth
            ? '관리자 권한을 확인하고 있습니다.'
            : user
              ? '현재 계정에는 방문자 통계 관리자 권한이 없습니다. 승인된 계정으로 다시 로그인해 주세요.'
              : '방문자 통계는 승인된 관리자만 확인할 수 있습니다. 관리자 이메일과 비밀번호를 입력해 주세요.'}
        </Description>

        {checkingAuth ? (
          <LoginButton disabled>권한 확인 중…</LoginButton>
        ) : user ? (
          <LoginButton disabled={submitting} onClick={handleSignOut}>
            {submitting ? '로그아웃 중…' : '다른 계정으로 로그인'}
          </LoginButton>
        ) : (
          <Form onSubmit={handleLogin}>
            <Field>
              <span>이메일</span>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="admin@example.com"
                autoComplete="username"
                required
              />
            </Field>
            <Field>
              <span>비밀번호</span>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="비밀번호 입력"
                autoComplete="current-password"
                required
              />
            </Field>
            <LoginButton type="submit" disabled={submitting || !email.trim() || !password}>
              {submitting ? '로그인 중…' : '이메일로 로그인'}
            </LoginButton>
          </Form>
        )}

        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        <SecurityNote>인증된 계정 중 별도 관리자 권한이 등록된 사용자만 통계 데이터에 접근할 수 있습니다.</SecurityNote>
      </LoginCard>
    </Page>
  );
};

export default UserCountLogin;
